import { createApiClient, type ClientEvents } from '@dbrain/api-client';
import { currentLocale } from '../i18n/index.js';
import { createCsrf } from './csrf.js';

export { ApiClientError, isApiError } from '@dbrain/api-client';

/**
 * The browser's API client: the shared client (@dbrain/api-client, Phase 15c) with the session cookie and the
 * double-submit CSRF token (ADR-0003). 16c.1: the token is this installation's own cookie only (csrf.ts).
 */
const csrf = createCsrf({
  cookies: () => document.cookie,
  fetch: async () => (await fetch('/api/auth/csrf', { credentials: 'same-origin' })).json() as Promise<{ token: string; cookie?: string }>,
});

const events: ClientEvents = {};
const client = createApiClient({ baseUrl: '', locale: currentLocale, auth: { kind: 'browser', csrfToken: csrf.token } }, events);

/**
 * Called when any request says the session is over (signed out elsewhere, idle, turned off by an
 * admin, refused by the company directory): the app goes back to the sign-in page at once.
 */
export function whenSessionEnds(handler: () => void): void {
  events.sessionEnded = handler;
}

/** Called when a write is refused because the license made Oppy read-only (D-124): the screens read the license again. */
export function whenLicenseRefuses(handler: () => void): void {
  events.licenseRefused = handler;
}

export const api = client.api;

/** Raw-body upload (files): same CSRF and error handling as api(). */
export const upload = <T,>(method: 'POST' | 'PUT', path: string, file: Blob, headers: Record<string, string> = {}): Promise<T> =>
  client.upload<T>(method, path, file, headers);
